import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, X, Presentation, FileText, CalendarCheck, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/tracker";
import { Calculator } from "@/components/Calculator";
import { FormationQR } from "@/components/FormationQR";
import { ModuleContactGate } from "@/components/ModuleContactGate";
import { BOOKING_URL } from "@/config";
import { useSeo } from "@/lib/seo";

export default function DeckViewer() {
  const { slug } = useParams();
  const [module, setModule] = useState(null);
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [gated, setGated] = useState(() => typeof window !== "undefined" && localStorage.getItem("mpp_contact_done") === "1");
  const touchStart = useRef(null);

  useSeo(module?.meta_title || module?.title, module?.meta_description || module?.description);

  useEffect(() => {
    api.moduleBySlug(slug).then((m) => {
      setModule(m);
      track("formation_view", { label: m.title, meta: { slug } });
      if (typeof window !== "undefined" && window.location.search.includes("ref=qr")) track("qr_scan", { label: m.title, meta: { slug } });
      api.slides(m.id).then(setSlides).catch(() => {});
    }).catch(() => {});
  }, [slug]);

  const total = slides.length;
  const go = useCallback((n) => { setOpenFaq(null); setIdx(Math.max(0, Math.min(total - 1, n))); }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(idx + 1);
      if (e.key === "ArrowLeft") go(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, go]);

  const onTouchStart = (e) => (touchStart.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (dx < -50) go(idx + 1);
    if (dx > 50) go(idx - 1);
    touchStart.current = null;
  };

  if (!module) return <div className="min-h-screen grid place-items-center text-slate-500">Chargement…</div>;
  if (!gated) return <ModuleContactGate moduleTitle={module.title} onDone={() => setGated(true)} />;

  const color = module.color || "#012141";
  const slide = slides[idx];
  const progress = total ? ((idx + 1) / total) * 100 : 0;

  return (
    <div data-testid="deck-viewer" className="min-h-screen flex flex-col bg-[#FDFDFD]">
      <div className="h-1.5 w-full bg-[#012141]/10">
        <div data-testid="deck-progress-bar" className="h-full transition-all duration-400" style={{ width: `${progress}%`, background: "#C89A3E" }} />
      </div>
      <div className="max-w-5xl w-full mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
        <Link to="/formations" data-testid="deck-back-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#012141]"><ChevronLeft size={18} /> <span className="hidden sm:inline">Formations</span></Link>
        <div className="text-center min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider" style={{ color: "#C89A3E" }}>{module.category}</div>
          <div className="text-sm font-semibold text-[#012141] truncate">{module.title}</div>
        </div>
        <div className="flex items-center gap-2">
          <FormationQR slug={module.slug} title={module.title} />
          <a href={api.exportUrl(module.id, "pptx")} data-testid="export-pptx-btn" title="Télécharger PPTX" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-white hover:brightness-110 transition-all" style={{ backgroundColor: "#012141" }}><Presentation size={14} /> <span className="hidden sm:inline">PPTX</span></a>
          <a href={api.exportUrl(module.id, "pdf")} data-testid="export-pdf-btn" title="Télécharger PDF" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-[#012141] border border-[#012141]/15 hover:bg-[#012141]/5 transition-all"><FileText size={14} /> <span className="hidden sm:inline">PDF</span></a>
          <Link to="/formations" className="grid place-items-center w-9 h-9 rounded-full text-slate-500 hover:bg-slate-100"><X size={18} /></Link>
        </div>
      </div>

      {module.banner_image && (
        <div className="max-w-5xl w-full mx-auto px-4 sm:px-8">
          <div data-testid="deck-banner" className="rounded-3xl overflow-hidden border border-[#012141]/10 shadow-sm">
            <img src={module.banner_image} alt={module.title} className="w-full h-32 sm:h-44 object-cover" />
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 py-6">
          {slide ? (
            <div key={idx} className="slide-enter rounded-3xl bg-white border border-[#012141]/10 shadow-[0_30px_60px_-30px_rgba(18,49,92,0.3)] p-8 sm:p-12">
              {slide.category_label && <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-4" style={{ color: "#C89A3E" }}>{slide.category_label}</div>}
              {slide.figure && <div className="mb-4 font-heading text-3xl sm:text-4xl font-bold" style={{ color }}>{slide.figure}</div>}
              <h1 data-testid="slide-title" className="text-2xl sm:text-4xl font-bold text-[#012141] font-heading leading-tight">{slide.title}</h1>
              {slide.text && <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{slide.text}</p>}

              {slide.bullets?.length > 0 && (
                <ul className="mt-6 space-y-3">
                  {slide.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700"><span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#C89A3E" }} /><span className="text-base leading-relaxed">{b}</span></li>
                  ))}
                </ul>
              )}

              {slide.media && <img src={slide.media} alt={slide.title} className="mt-6 rounded-2xl max-h-80 w-auto object-contain shadow" />}

              {slide.calculator && <div className="mt-6"><Calculator type={slide.calculator} accent={color} /></div>}

              {slide.faq?.length > 0 && (
                <div className="mt-6 space-y-2" data-testid="slide-faq">
                  {slide.faq.map((f, i) => (
                    <div key={i} className="rounded-xl border border-[#012141]/10 overflow-hidden">
                      <button data-testid={`faq-toggle-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left font-semibold text-[#012141] hover:bg-[#012141]/5">
                        <span className="text-sm">{f.q}</span>
                        <ChevronDown size={16} className={`flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                      </button>
                      {openFaq === i && <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</div>}
                    </div>
                  ))}
                </div>
              )}

              {slide.is_cta && (
                <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="slide-cta-booking" className="mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#C89A3E] text-[#012141] font-bold hover:brightness-95 transition-all"><CalendarCheck size={18} /> Prendre un rendez-vous</a>
              )}
            </div>
          ) : (
            <div className="text-center text-slate-400 py-24">Ce module n'a pas encore de slides.</div>
          )}
        </div>
      </div>

      <div className="max-w-5xl w-full mx-auto px-4 sm:px-8 py-6 flex items-center justify-between gap-4">
        <button data-testid="deck-prev-slide-btn" onClick={() => go(idx - 1)} disabled={idx === 0} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-[#012141] bg-white border border-[#012141]/15 disabled:opacity-40 hover:bg-[#012141]/5 transition-all"><ArrowLeft size={18} /> <span className="hidden sm:inline">Précédent</span></button>
        <div className="flex items-center gap-1.5 flex-wrap justify-center max-w-[50%]">
          {slides.map((_, i) => (
            <button key={i} data-testid={`deck-dot-${i}`} onClick={() => go(i)} aria-label={`Slide ${i + 1}`} className="h-2.5 rounded-full transition-all duration-300" style={{ width: i === idx ? 24 : 9, backgroundColor: i === idx ? "#C89A3E" : "#CBD5E1" }} />
          ))}
          <span data-testid="deck-counter" className="ml-3 text-sm font-bold text-slate-500">{total ? idx + 1 : 0} / {total}</span>
        </div>
        <button data-testid="deck-next-slide-btn" onClick={() => go(idx + 1)} disabled={idx >= total - 1} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white disabled:opacity-40 hover:brightness-110 transition-all" style={{ backgroundColor: "#012141" }}><span className="hidden sm:inline">Suivant</span> <ArrowRight size={18} /></button>
      </div>
    </div>
  );
}
